import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// Upgraded deprecated legacy package icon paths to match premium system libraries
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { useForm } from '../../../pages/General/useForm';
import { updatePassword } from './passwordSlice';

// Reusable standard configuration layout dictionary for production outlined text inputs
const enterprisePasswordInputStyles = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#FFFFFF',
        transition: 'all 0.2s ease-in-out',
        border: '1px solid #E2E8F0',
        '& fieldset': { border: 'none' },
        '&:hover': {
            borderColor: '#CBD5E1',
            boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
        },
        '&.Mui-focused': {
            borderColor: '#1976d2', // --- CORPORATE IDENTITY BRAND BLUE ---
            boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.08)'
        }
    },
    '& .MuiInputLabel-outlined': {
        color: '#64748B',
        fontWeight: 500,
        transform: 'translate(14px, 10px) scale(1)',
        '&.Mui-focused, &.MuiInputLabel-shrink': {
            transform: 'translate(14px, -8px) scale(0.75)',
            color: '#1976d2',
            fontWeight: 600
        }
    },
    '& input': {
        padding: '10px 14px !important',
        fontSize: '13.5px',
        color: '#1E293B',
        fontWeight: 500
    },
    '& .MuiFormHelperText-root': {
        mx: 0.5,
        mt: 0.75,
        fontWeight: 500
    }
};

const PasswordPage = () => {
    const dispatch = useDispatch();

    const password = useSelector(state => state.password);
    const profile = useSelector(state => state.auth.profile);    
    const [alert, setAlert] = useState(false);
    const [open, setOpen] = useState(false);
    const [checkFormSubmit, setCheckFormSubmit] = useState(false);
    const [visible, SetVisible] = useState(false)
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);      
    
    const handleClickShowPassword = () => setShowPassword(!showPassword);
    const handleMouseDownPassword = () => setShowPassword(!showPassword);
    const handleClickshowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);
    const handleMouseDownshowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);
    const handleClickshowCurrentPassword = () => setShowCurrentPassword(!showCurrentPassword);
    const handleMouseDownshowCurrentPassword = () => setShowCurrentPassword(!showCurrentPassword);
    
    const initialFValues = {
        current_password: '',
        password: '',
        confirm_password: ''
    }

    const validate = (fieldValues = values) => {
        let temp = { ...errors }
        if ('current_password' in fieldValues)
            temp.current_password = fieldValues.current_password ? "" : "This field is required."
        if ('password' in fieldValues)
            temp.password = fieldValues.password ? "" : "This field is required."
        if ('confirm_password' in fieldValues)
            temp.confirm_password = fieldValues.confirm_password ? "" : "This field is required."

        setErrors({
            ...temp
        })

        if (fieldValues == values)
            return Object.values(temp).every(x => x == "")
    }

    const handleClose = () => {
        setOpen(false)
    };

    const handleSubmit = e => {
        e.preventDefault()
        if (validate()) {
            setCheckFormSubmit(true)
            console.log(values)
            dispatch(updatePassword(profile && profile.id,values))
        }
    }

    const {
        values,
        setValues,
        errors,
        setErrors,
        handleInputChange
    } = useForm(initialFValues, validate, true);

    useEffect(() => {  
        if (checkFormSubmit && password && password.hasErrors===false) {
            setValues({
                ...values,
                current_password: "",            
                password: "",
                confirm_password: "",
            })
        }
        if (checkFormSubmit && password && password.message) {
            setAlert(true)
            setOpen(true)
        }
    }, [password]);

    return (
        <Box className="sc--settings--card" sx={{ width: '100%', boxSizing: 'border-box', py: 1 }}>
            
            {/* Realtime Async Transaction Overlay Mask Component Layer */}
            {password && password.loading === true && (
                <Backdrop
                    sx={{ 
                        color: '#1976d2', 
                        zIndex: (theme) => theme.zIndex.drawer + 1,
                        backgroundColor: 'rgba(248, 250, 252, 0.7)',
                        backdropFilter: 'blur(3px)'
                    }}
                    open={true}
                >
                    <CircularProgress color="inherit" size={40} thickness={4.5} />
                </Backdrop>
            )}

            {/* Inbound Process Status Feedback Alert Banner */}
            {password && password.loading === false && alert && (
                <Snackbar
                    open={open}
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "right"
                    }}
                    autoHideDuration={6000}
                    onClose={handleClose}
                    sx={{ mt: 2 }}
                >
                    <Alert
                        severity={password.hasErrors == true ? "error" : "success"}
                        onClose={handleClose}
                        variant="filled"
                        sx={{ 
                            width: '100%', 
                            borderRadius: '12px',
                            fontWeight: 600,
                            boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)',
                            fontSize: '13px',
                            alignItems: 'center'
                        }}
                    >
                        {password.message}
                    </Alert>
                </Snackbar>
            )}

            {/* Core Card Document Workspace Surface */}
            {password && password.loading === false && (
                <Card 
                    className="sc--cardhead--details"
                    sx={{ 
                        width: '100%',
                        maxWidth: 540,
                        mx: 'auto',
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E2E8F0',
                        borderRadius: '16px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02)',
                        overflow: 'hidden'
                    }}
                >
                    <CardHeader 
                        title={
                            <Typography variant="h6" component="h1" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '16px', m: 0 }}>
                                Change Password
                            </Typography>
                        } 
                        sx={{ p: 2.5, backgroundColor: '#FFFFFF' }}
                    />
                    
                    <Divider sx={{ borderColor: '#E2E8F0' }} />
                    
                    <CardContent sx={{ p: 4, boxSizing: 'border-box' }} component="form" onSubmit={handleSubmit} noValidate>
                        <Stack spacing={3.5}>
                            
                            {/* 1. Account Current Password Secure Validation Field */}
                            <Box className="sc--form--center" sx={{ width: '100%', minHeight: '44px' }}>
                                <TextField
                                    label='Current Password'
                                    size='small'
                                    required 
                                    type={showCurrentPassword ? "text" : "password"}
                                    name="current_password"
                                    value={values.current_password}
                                    variant="outlined"
                                    onChange={handleInputChange}
                                    error={!!errors.current_password}
                                    helperText={errors.current_password}
                                    sx={enterprisePasswordInputStyles}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end" sx={{ marginRight: '-4px' }}>
                                                <IconButton
                                                    aria-label="toggle current password visibility"
                                                    onClick={handleClickshowCurrentPassword}
                                                    onMouseDown={handleMouseDownshowCurrentPassword}
                                                    edge="end"
                                                    sx={{ color: '#64748B', p: '6px', '&:hover': { backgroundColor: '#F1F5F9' } }}
                                                >
                                                    {showCurrentPassword ? <Visibility sx={{ fontSize: '18px' }} /> : <VisibilityOff sx={{ fontSize: '18px' }} />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }} 
                                />
                            </Box>

                            {/* 2. New Identity Credentials Target Mutation Field */}
                            <Box className="sc--form--center" sx={{ width: '100%', minHeight: '44px' }}>
                                <TextField
                                    label='New Password'
                                    size='small'
                                    name="password"
                                    required 
                                    type={showPassword ? "text" : "password"}
                                    value={values.password}
                                    onChange={handleInputChange}
                                    error={!!errors.password}
                                    variant="outlined"
                                    helperText={errors.password}
                                    sx={enterprisePasswordInputStyles}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end" sx={{ marginRight: '-4px' }}>
                                                <IconButton
                                                    aria-label="toggle new password visibility"
                                                    onClick={handleClickShowPassword}
                                                    onMouseDown={handleMouseDownPassword}
                                                    edge="end"
                                                    sx={{ color: '#64748B', p: '6px', '&:hover': { backgroundColor: '#F1F5F9' } }}
                                                >
                                                    {showPassword ? <Visibility sx={{ fontSize: '18px' }} /> : <VisibilityOff sx={{ fontSize: '18px' }} />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }} 
                                />
                            </Box>

                            {/* 3. Terminal Password Match Confirmation Entry Field */}
                            <Box className="sc--form--center" sx={{ width: '100%', minHeight: '44px' }}>
                                <TextField
                                    label='Confirm Password'
                                    size='small'
                                    name="confirm_password"
                                    required 
                                    type={showConfirmPassword ? "text" : "password"}
                                    value={values.confirm_password}
                                    onChange={handleInputChange}
                                    error={!!errors.confirm_password}                                
                                    helperText={errors.confirm_password}
                                    variant="outlined"
                                    sx={enterprisePasswordInputStyles}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end" sx={{ marginRight: '-4px' }}>
                                                <IconButton
                                                    aria-label="toggle confirm password visibility"
                                                    onClick={handleClickshowConfirmPassword}
                                                    onMouseDown={handleMouseDownshowConfirmPassword}
                                                    edge="end"
                                                    sx={{ color: '#64748B', p: '6px', '&:hover': { backgroundColor: '#F1F5F9' } }}
                                                >
                                                    {showConfirmPassword ? <Visibility sx={{ fontSize: '18px' }} /> : <VisibilityOff sx={{ fontSize: '18px' }} />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }} 
                                />
                            </Box>

                            {/* 4. Submission Pipeline Operation Control Panel Trigger Footer */}
                            <Box className="sc--form--center" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pt: 1, borderTop: '1px solid #E2E8F0' }}>
                                <Button 
                                    variant="contained" 
                                    type="submit"
                                    sx={{
                                        textTransform: 'none',
                                        borderRadius: '10px',
                                        fontWeight: 700,
                                        fontSize: '13.5px',
                                        height: '38px',
                                        px: 4,
                                        boxShadow: 'none',
                                        backgroundColor: '#1976d2',
                                        color: '#FFFFFF',
                                        '&:hover': { backgroundColor: '#115293', boxShadow: 'none' }
                                    }}
                                >
                                    Update Password
                                </Button> 
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
            )}
        </Box>
    )
}

export default PasswordPage;